import type { MergedBranchRepresentationType } from '@readme/api/src/mappings/merge/types';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import useClassy from '@core/hooks/useClassy';
import useIsDocumentReady from '@core/hooks/useIsDocumentReady';
import useLocalStorage from '@core/hooks/useLocalStorage';
import useUniqueId from '@core/hooks/useUniqueId';
import { useSuperHubStore } from '@core/store';
import type { APIErrorType } from '@core/types';

import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Modal, { ModalBody, ModalFooter } from '@ui/Modal';
import { notify } from '@ui/Notification';
import Toggle from '@ui/Toggle';
import Truncate from '@ui/Truncate';

import { useBlockingModalContext } from '..';
import VersionNotification from '../../components/Notification';
import useBuildBranchPath from '../../hooks/useBuildBranchPath';

import { useMergeBranchModalContext } from './Context';
import styles from './index.module.scss';

function SuccessNotification({ mergedBranch }: { mergedBranch: MergedBranchRepresentationType }) {
  const currentBranch = useSuperHubStore(s => s.currentBranch);
  const buildVersionPath = useBuildBranchPath();

  return (
    <VersionNotification
      description={
        <>
          Merged branch into <Truncate maxWidth={100}>v{mergedBranch.upstream}</Truncate>.
          {mergedBranch.upstream !== currentBranch.name && (
            <>
              {' '}
              <a href={buildVersionPath(mergedBranch.upstream)} rel="noreferrer">
                View now
              </a>
            </>
          )}
        </>
      }
      kind="success"
      title="Branch Merged"
    />
  );
}

const LOCAL_STORAGE_KEY = 'merge-branch-cleanup';

export default function MergeBranchModal() {
  const bem = useClassy(styles, 'MergeBranchModal');
  const uid = useUniqueId('MergeBranchModal');
  const modalContainerId = uid('modal');
  const modalRef = useRef<Modal>(null);
  const isClient = useIsDocumentReady();

  const [currentBranchName, mergeBranch] = useSuperHubStore(s => [s.currentBranch.name, s.branches.mergeBranch]);
  const buildVersionPath = useBuildBranchPath();
  const { branchToMerge, mergeStatus, setBranchToMerge } = useMergeBranchModalContext();
  const { setBlockingModalValue } = useBlockingModalContext();
  const [isMerging, setIsMerging] = useState(false);
  const [mergeError, setMergeError] = useState<APIErrorType | null>(null);

  const storage = useLocalStorage({ json: true });
  const [cleanupBranch, setCleanupBranch] = useState(storage.getItem(LOCAL_STORAGE_KEY) || false);
  useEffect(() => {
    storage.setItem(LOCAL_STORAGE_KEY, cleanupBranch);
  }, [cleanupBranch, storage]);

  const sourceBranchName = useMemo(() => `v${branchToMerge?.name}`, [branchToMerge?.name]);
  const targetBranchName = useMemo(() => `v${branchToMerge?.base.name}`, [branchToMerge?.base.name]);
  const hasMergeConflict = useMemo(() => mergeStatus?.status === 'conflict', [mergeStatus?.status]);
  const externalDiffUrl = useMemo(() => branchToMerge?.href.external.diff || '', [branchToMerge?.href.external.diff]);

  useEffect(() => {
    modalRef.current?.toggle(!!branchToMerge);

    // When closing the modal, reset all other states.
    if (!branchToMerge) {
      setIsMerging(false);
      setMergeError(null);
    }
  }, [branchToMerge]);

  const handleMerge = useCallback(async () => {
    if (!branchToMerge) return;
    try {
      setIsMerging(true);
      const response = await mergeBranch(branchToMerge.name, { cleanupBranch });
      setBranchToMerge(null);
      notify(<SuccessNotification mergedBranch={response.data} />);

      // Redirect to parent version after merging the current branch.
      if (currentBranchName === branchToMerge.name) {
        setBlockingModalValue('Redirecting…');
        window.location.assign(buildVersionPath(branchToMerge.base.name));
      }
    } catch (error) {
      setMergeError(error);
    } finally {
      setIsMerging(false);
    }
  }, [
    branchToMerge,
    buildVersionPath,
    cleanupBranch,
    currentBranchName,
    mergeBranch,
    setBlockingModalValue,
    setBranchToMerge,
  ]);

  const mergeErrorMessages = useMemo(() => {
    const errors: { key: string; message: string }[] = [];

    if (mergeError?.info?.detail) {
      errors.push({ key: 'detail-error', message: mergeError.info.detail });
    }

    if (Array.isArray(mergeError?.info?.errors)) {
      errors.push(...mergeError.info.errors);
    }

    return errors;
  }, [mergeError?.info?.detail, mergeError?.info?.errors]);

  return (
    <>
      <Modal
        ref={modalRef}
        className={bem('-container')}
        noDismiss
        size="sm"
        target={`#${modalContainerId}`}
        verticalCenter
      >
        <ModalBody className={bem('-body')}>
          <h1 className={bem('-title')}>{hasMergeConflict ? <>Merging With Conflicts</> : <>Merging Branch</>}</h1>

          <p className={bem('-description')}>
            {hasMergeConflict ? (
              <>
                Conflicts will overwrite changes in the parent version. All other changes will be merged together with
                the parent version. You cannot undo this action.
              </>
            ) : (
              <>Changes will be merged into the parent version. You cannot undo this action.</>
            )}
          </p>

          <Flex align="center" className={bem('-source-target')} gap="xs" justify="center">
            <Truncate className={bem('-source-target-field')}>{sourceBranchName}</Truncate>
            <Icon
              aria-label="Merge to"
              className={bem('-source-target-icon')}
              color="color-text-minimum"
              name="arrow-right"
            />
            <Truncate className={bem('-source-target-field')}>{targetBranchName}</Truncate>
          </Flex>

          {!!externalDiffUrl && (
            <a className={bem('-diff-link')} href={externalDiffUrl} rel="noreferrer" target="_blank">
              Diff in GitHub
              <Icon aria-hidden="true" className={bem('-diff-link-icon')} name="arrow-up-right" />
            </a>
          )}

          <Toggle
            checked={cleanupBranch}
            className={bem('-cleanup-toggle')}
            onChange={() => {
              setCleanupBranch(prev => !prev);
            }}
            type="checkbox"
          >
            <Flex align="center" className={bem('-cleanup-toggle-label')} gap="xs" layout="col">
              Delete branch after merging
            </Flex>
          </Toggle>

          {!!cleanupBranch && (
            <div className={bem('-cleanup-warning')}>
              This action cannot be undone. If any issues occur from merging, content cannot be recovered.
            </div>
          )}

          {!!mergeError && (
            <div className={bem('-error')}>
              {!!mergeError && <span>Error occurred when merging. {mergeError.status !== 409 && 'Try again?'}</span>}
              {mergeErrorMessages?.map(error => (
                <span key={error.key}>{error.message.endsWith('.') ? error.message : `${error.message}.`}</span>
              ))}
            </div>
          )}
        </ModalBody>

        <ModalFooter align="center" gap="sm" justify="center">
          <Button ghost kind="minimum" onClick={() => setBranchToMerge(null)}>
            Cancel
          </Button>
          {hasMergeConflict ? (
            <Button kind="destructive" loading={isMerging ? 'Merging' : false} onClick={handleMerge}>
              Overwrite & Merge
            </Button>
          ) : (
            <Button
              aria-label={cleanupBranch ? 'Merge and Delete' : 'Merge'}
              className={bem('-primary-cta')}
              kind={cleanupBranch ? 'destructive' : undefined}
              loading={isMerging ? 'Merging' : false}
              onClick={handleMerge}
            >
              {cleanupBranch ? 'Merge and Delete' : 'Merge'}
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {isClient
        ? createPortal(
            <div
              className={bem('&', 'ModalWrapper')}
              data-color-mode="dark"
              data-testid={bem()}
              id={modalContainerId}
            />,
            document.body,
          )
        : null}
    </>
  );
}

export * from './Context';
